<script setup lang="ts">
import type { Component } from 'vue';

export interface IconSizeToggleOption {
  value: string;
  text: string;
  icon: Component;
  iconSize?: number;
}

defineProps<{
  options: IconSizeToggleOption[];
  modelValue: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const selectOption = (value: string) => {
  emit('update:modelValue', value);
};
</script>

<template>
  <div class="icon-size-toggle">
    <button
      v-for="option in options"
      :key="option.value"
      type="button"
      class="icon-size-toggle-item"
      :class="{ 'is-selected': option.value === modelValue }"
      :title="option.text"
      :aria-label="option.text"
      @click="selectOption(option.value)"
    >
      <component :is="option.icon" :size="option.iconSize ?? 16" weight="fill" />
    </button>
  </div>
</template>

<style scoped>
.icon-size-toggle {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.125rem;
  /* Matches the create button's own height (--handler-control-height, set on the shared toolbar
     row in FileExplorerHandler.vue) so the two controls sit flush at the same height. Vertical
     padding is folded into that fixed height rather than added on top of it — see
     .icon-size-toggle-item's height for the resulting content area. */
  height: var(--handler-control-height);
  padding: 0 0.25rem;
  box-sizing: border-box;
  background-color: #f5f5f7;
  border-radius: 999px;
}

.icon-size-toggle-item {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  background: none;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #6b7280;
}

.icon-size-toggle-item:hover {
  color: var(--primary-color);
}

.icon-size-toggle-item.is-selected {
  background-color: color-mix(in srgb, var(--primary-color) 15%, transparent);
  color: var(--primary-color);
}
</style>
